import { useEffect, useRef, useState } from 'react';
import { EmojiPicker } from './ReactionBar';
import { GifPicker } from './GifPicker';
import { MAX_FILES_PER_MESSAGE } from '@avslack/shared';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import type { GifResult, PublicUser, RichTextDoc, UserGroup } from '@avslack/shared';
import { RichComposer, type ComposerApi, type ComposerSubmit } from './RichComposer';
import { useUploads } from '../data/uploads';
import { messagePlainText, type LocalMessage } from '../data/messages';
import { takePendingShare } from '../lib/share-inbox';
import { useUsers } from '../data/users';
import { UploadTray } from './Attachments';
import { Icon } from './Icon';
import { ScheduleDialog, type ScheduleRepeat } from './ScheduleDialog';

interface ComposerProps {
  /** Melyik csatornae - a mas appbol megosztott tartalom ide toltodik be (K2). */
  channelId?: string;
  placeholder: string;
  users: PublicUser[];
  groups: UserGroup[];
  meId: string;
  disabled?: boolean;
  disabledReason?: string;
  /** Szerver oldalon tarolt piszkozat - a szerkeszto ebbol indul. */
  initialDraft?: RichTextDoc | null;
  /** Mire valaszolunk: az idezet a szerkeszto fole kerul. */
  replyingTo?: LocalMessage | null;
  onCancelReply?: () => void;
  /**
   * A harmadik ertek a FONTOS jeloles.
   *
   * Nem a dokumentum resze: a `ComposerSubmit` a leirt tartalom, ez pedig az
   * uzenet kiseroje - ugyanugy, mint a mellekletek listaja.
   */
  onSend: (value: ComposerSubmit, fileIds: string[], isImportant: boolean, isTask: boolean) => void;
  onTyping?: () => void;
  onDraftChange?: (value: { text: string; body?: RichTextDoc | undefined }) => void;
  /** Szavazas inditasa (U4) - hianyaban nincs "Szavazás" gomb. */
  onPoll?: () => void;
  /** Hianyaban nincs "Kuldes kesobb" menu (pl. az oleles chatjeben). */
  onSchedule?: (
    value: ComposerSubmit,
    sendAt: Date,
    isImportant: boolean,
    repeat?: ScheduleRepeat,
  ) => Promise<void> | void;
  /**
   * KESKENY valtozat - az oleles chat-paneljebe.
   *
   * Ugyanaz a szerkeszto, ugyanazokkal a kepessegekkel; csak a keret kisebb, es
   * a billentyu-sugo marad el, ami egy 320 keppontos panelben harom sorba
   * tordelne.
   */
  compact?: boolean;
}

/** Ennyi szunet utan mentjuk a piszkozatot - gepeles kozben nem hivogatjuk a szervert. */
const DRAFT_DEBOUNCE_MS = 1200;

/**
 * Idozitesi ajanlatok - a GYAKORI esetek.
 *
 * Ami nem fer bele, azt az "Egyedi idopont" pontja intezi: ott konkret datum
 * es ora adhato meg. A szerver eddig is barmilyen idopontot elfogadott (egy
 * perc es 90 nap kozott), csak a valaszto hianyzott hozza.
 */
const SCHEDULE_PRESETS = [
  { label: '10 perc múlva', minutes: 10 },
  { label: '1 óra múlva', minutes: 60 },
  { label: 'Holnap reggel 8-kör', minutes: null as number | null, tomorrowAt: 8 },
  { label: 'Hétfő reggel 8-kör', minutes: null as number | null, nextMondayAt: 8 },
];

function resolvePreset(preset: (typeof SCHEDULE_PRESETS)[number]): Date {
  const date = new Date();

  if (preset.minutes) {
    date.setMinutes(date.getMinutes() + preset.minutes);
    return date;
  }

  if (preset.tomorrowAt !== undefined) {
    date.setDate(date.getDate() + 1);
    date.setHours(preset.tomorrowAt, 0, 0, 0);
    return date;
  }

  if (preset.nextMondayAt !== undefined) {
    const daysUntilMonday = (8 - date.getDay()) % 7 || 7;
    date.setDate(date.getDate() + daysUntilMonday);
    date.setHours(preset.nextMondayAt, 0, 0, 0);
    return date;
  }

  return date;
}

export function Composer({
  channelId,
  placeholder,
  users,
  groups,
  meId,
  disabled,
  disabledReason,
  initialDraft,
  replyingTo,
  onCancelReply,
  onSend,
  onTyping,
  onDraftChange,
  onSchedule,
  onPoll,
  compact = false,
}: ComposerProps) {
  const apiRef = useRef<ComposerApi | null>(null);
  const draftTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement | null>(null);
  const [scheduling, setScheduling] = useState(false);
  const [dragging, setDragging] = useState(false);
  /**
   * FONTOS jeloles a kovetkezo uzenetre.
   *
   * Kuldes utan visszaall - nem "uzemmod", hanem egyetlen uzenetre szolo
   * dontes. Kulonben a bejelolve felejtett kapcsolo minden tovabbi
   * hozzaszolast fontosnak kialtana ki, es a jeloles ertektelenne valna.
   */
  const [important, setImportant] = useState(false);
  /** FELADAT a kovetkezo uzenet - a "Fontos" melletti jelolo; kuldes utan visszaall. */
  const [task, setTask] = useState(false);
  const uploads = useUploads();
  const { nameOf } = useUsers();

  useEffect(
    () => () => {
      if (draftTimer.current) clearTimeout(draftTimer.current);
    },
    [],
  );

  /*
    MAS APPBOL MEGOSZTOTT tartalom (K2, lib/share-inbox.ts): a szoveg a
    szerkesztobe, a fajlok a csatolmanyok koze. Elkuldeni a felhasznalo fogja.
  */
  useEffect(() => {
    if (!channelId) return;
    const shared = takePendingShare(channelId);
    if (!shared) return;
    if (shared.text) apiRef.current?.insert(shared.text);
    if (shared.files.length > 0) void uploads.add(shared.files);
    requestAnimationFrame(() => apiRef.current?.focus());
    // Csak az elso betolteskor: a megosztas egyszer hasznalhato fel.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelId]);

  /*
    Valasz inditasakor a kurzor rogton a szerkesztobe kerul.

    A valasz gomb a folyamban van, jo messze a beviteli mezotol; enelkul minden
    valasz egy felesleges kattintassal kezdodne.
  */
  useEffect(() => {
    if (replyingTo) apiRef.current?.focus();
  }, [replyingTo]);

  const handleChange = (value: ComposerSubmit) => {
    if (draftTimer.current) clearTimeout(draftTimer.current);
    if (!onDraftChange) return;
    draftTimer.current = setTimeout(() => onDraftChange(value), DRAFT_DEBOUNCE_MS);
  };

  /**
   * A kivalasztott GIF - a kephez hasonloan ELOSZOR a kuldendo mezobe kerul.
   *
   * Korabban a valaszto azonnal elkuldte. Egy felreklikkelt GIF igy mar kint
   * volt, es a mellé irt szoveg kulon uzenetbe kerult. Most a GIF a szoveggel
   * egyutt, egy uzenetben megy el - Enterrel vagy a kuldes gombbal.
   */
  const [pendingGif, setPendingGif] = useState<GifResult | null>(null);

  /*
    KULDES A FELTOLTES VEGEN.

    Ha a kep meg tolt, amikor Entert nyomnak, a kuldes varjon - kulonben a fajl
    lemaradna az uzenetrol. Korabban ilyenkor a szerkeszto megis kiurult, es a
    kozben begepelt szoveg ELVESZETT (2026-11-04-i hibajelzes). Most a szoveg
    marad, es amint a feltoltes kesz, az uzenet magatol elmegy, a szoveggel
    egyutt. Ha a feltoltes elbukik, nem kuldunk: a szoveg ott van, a hiba latszik.
  */
  const [sendWhenReady, setSendWhenReady] = useState(false);
  const uploadFailed = uploads.uploads.some((u) => u.error);
  useEffect(() => {
    if (!sendWhenReady || uploads.isBusy) return;
    setSendWhenReady(false);
    if (!uploadFailed) apiRef.current?.submit();
  }, [sendWhenReady, uploads.isBusy, uploadFailed]);

  const send = (value: ComposerSubmit): boolean => {
    if (uploads.isBusy) {
      setSendWhenReady(true);
      return false;
    }

    if (draftTimer.current) clearTimeout(draftTimer.current);
    onSend(pendingGif ? withGif(value, pendingGif) : value, uploads.readyFileIds, important, task);
    setPendingGif(null);
    uploads.clear();
    setImportant(false);
    setTask(false);
    // A piszkozat elkuldes utan feleslegesse valik.
    onDraftChange?.({ text: '' });
    return true;
  };

  /**
   * Fajlvalasztas es rahuzas - mindketto ide fut be.
   *
   * A csatolas utan a kurzor VISSZAKERUL a szerkesztobe: a fajl a legritkabb
   * esetben all onmagaban, szinte mindig irunk melle egy mondatot. Eddig ehhez
   * kulon vissza kellett kattintani a mezobe.
   *
   * Nem varjuk meg a feltoltes vegét: gepelni mar akkor is lehet, amikor a
   * fajl meg tolt. (A kuldes viszont megvarja - lasd `send`.)
   *
   * A fokuszt a KOVETKEZO kepkockaban kerjuk. A fajlvalasztot bezarva a
   * bongeszo maga is rendezi a fokuszt; ha ugyanabban a korben szolnank,
   * elofordulna, hogy a mienk erkezik elobb, es a bongeszoe irja felul.
   */
  const pickFiles = (list: FileList | null) => {
    const chosen = Array.from(list ?? []);
    if (chosen.length === 0) return;

    void uploads.add(chosen);
    requestAnimationFrame(() => apiRef.current?.focus());
  };

  /** Nyitva van-e az egyedi idopont valasztoja - egyszeri vagy ismetlodo uzenethez. */
  const [customSchedule, setCustomSchedule] = useState<false | 'once' | 'repeat'>(false);

  const schedule = async (sendAt: Date, repeat?: ScheduleRepeat) => {
    const value = apiRef.current?.getValue();
    if (!value || scheduling || !onSchedule) return;

    setScheduling(true);
    try {
      await onSchedule(value, sendAt, important, repeat);
      setImportant(false);
      apiRef.current?.clear();
      if (draftTimer.current) clearTimeout(draftTimer.current);
      onDraftChange?.({ text: '' });
    } finally {
      setScheduling(false);
    }
  };

  if (disabled) {
    return (
      <div className="px-4 pb-4 sm:px-6">
        <p className="mx-auto max-w-4xl rounded-card border border-dashed border-border-soft px-4 py-3.5 text-center text-sm text-ink-muted">
          {disabledReason ?? 'Ide nem lehet írni.'}
        </p>
      </div>
    );
  }

  return (
    <div className={compact ? 'p-2' : 'px-4 pb-3 sm:px-6'}>
      <div className={compact ? '' : 'mx-auto max-w-4xl'}>
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={(e) => {
            // Csak akkor engedjuk el, ha tenyleg elhagytuk a dobozt.
            if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragging(false);
          }}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            pickFiles(e.dataTransfer.files);
          }}
          onPaste={(e) => {
            const pasted = Array.from(e.clipboardData.files);
            if (pasted.length > 0) {
              e.preventDefault();
              // Beillesztesnel a kurzor mar a szerkesztoben van - csak toltunk.
              void uploads.add(pasted);
            }
          }}
          className={[
            'rounded-card border bg-surface-raised px-3 py-2 shadow-(--shadow-soft)',
            'transition-[border-color,background-color,box-shadow] duration-(--duration-base) ease-(--ease-soft)',
            dragging
              ? 'border-accent border-dashed bg-accent-soft/40'
              : important
                ? /*
                    A SZERKESZTO IS kek lesz, amint bejeloltuk.

                    Igy nem kell a jelolonegyzetre neznie annak, aki epp gepel:
                    a mezo szine mondja meg, milyen uzenet fog elindulni.
                  */
                  'border-accent bg-accent-soft/30 shadow-(--shadow-lift)'
                : 'border-border-soft focus-within:border-accent focus-within:shadow-(--shadow-lift)',
          ].join(' ')}
        >
          {/*
            MIRE VALASZOLUNK.

            A Messenger mintaja: az idezet ide kerul, a szerkeszto fole, es a
            valasz a folyam vegen jelenik meg. Igy latszik gepeles kozben is,
            hogy mihez szolunk hozza - es egy kattintassal el is engedheto.
          */}
          {replyingTo && (
            <div className="mb-2 flex items-center gap-2 rounded-control border-l-2 border-accent bg-accent-soft/40 py-1.5 pl-2.5 pr-1.5">
              <span aria-hidden="true" className="shrink-0 text-accent">
                <Icon name="valasz" size="sm" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-2xs font-semibold text-accent">
                  Válasz neki: {nameOf(replyingTo.userId)}
                </span>
                <span className="block truncate text-xs text-ink-muted">
                  {messagePlainText(replyingTo) || 'Melléklet'}
                </span>
              </span>
              <button
                type="button"
                onClick={onCancelReply}
                title="Válasz elvetése"
                aria-label="Válasz elvetése"
                className="grid size-6 shrink-0 place-items-center rounded-control text-ink-faint transition-colors duration-(--duration-quick) hover:bg-surface-hover hover:text-ink"
              >
                <Icon name="bezaras" size="sm" />
              </button>
            </div>
          )}

          <UploadTray uploads={uploads.uploads} onRemove={uploads.remove} />
          {sendWhenReady && (
            <p role="status" className="mb-2 flex items-center gap-2 text-xs text-ink-muted">
              Elküldjük, amint a feltöltés kész.
              <button
                type="button"
                onClick={() => setSendWhenReady(false)}
                className="text-ink-faint underline hover:text-ink"
              >
                Mégse
              </button>
            </p>
          )}

          {/*
            A GIF elonezete PONTOSAN akkora, amekkora a csatornaban lesz (ugyanaz a
            meretezes, mint a RichText Gif-jenel): kuldes elott latszik, mi megy ki.
          */}
          {pendingGif && (
            <div className="mb-2 border-b border-border-soft pb-2">
              <div
                /*
                  A keskeny panelben kisebb elonezet: teljes meretben eltakarna
                  a hivas chatjet, amig a kuldesre var.
                */
                className={`relative w-full ${compact ? 'max-w-40' : 'max-w-sm'}`}
              >
                <img
                  src={pendingGif.url}
                  alt={pendingGif.title ?? 'GIF'}
                  width={pendingGif.width || undefined}
                  height={pendingGif.height || undefined}
                  referrerPolicy="no-referrer"
                  className="h-auto w-full rounded-control border border-border-soft"
                />
                <button
                  type="button"
                  onClick={() => setPendingGif(null)}
                  aria-label="GIF eltávolítása"
                  className="absolute right-1.5 top-1.5 grid size-7 place-items-center rounded-full bg-black/60 text-white transition-colors hover:bg-black/80"
                >
                  <Icon name="bezaras" size="sm" />
                </button>
              </div>
              <p className="mt-1 text-2xs text-ink-muted">
                Enterrel küldheted, a szöveggel együtt.
              </p>
            </div>
          )}

          {dragging && (
            <p className="pb-2 text-center text-sm font-medium text-accent">
              Engedd el a fájlokat a csatoláshoz
            </p>
          )}

          {/*
            NINCS `key` a piszkozat meglete szerint.

            Korabban `key={initialDraft ? 'with-draft' : 'empty'}` allt itt, hogy
            a kesobb betolto piszkozat bekeruljon a szerkesztobe. Csakhogy a
            piszkozat mentese MAGA billentette at a kulcsot: az elso leutes utan
            a csatornanak lett piszkozata, a kulcs `empty`-rol `with-draft`-ra
            valtott, a React pedig lebontotta es ujraepitette az egesz
            szerkesztot - IRAS KOZBEN, a fokusszal egyutt. Kuldes utan
            ugyanez forditva.

            A piszkozatot ezert most egyszer, a szerkeszto belsejeben toltjuk be
            (lasd `RichComposer`), a csatornankenti elkulonitest pedig a hivo
            oldali `key={channelId}` adja.
          */}
          <RichComposer
            placeholder={placeholder}
            users={users}
            groups={groups}
            meId={meId}
            initial={initialDraft ?? undefined}
            onSubmit={send}
            onTyping={onTyping}
            onChange={handleChange}
            compact={compact}
            allowEmpty={uploads.readyFileIds.length > 0 || pendingGif !== null}
            onReady={(api) => {
              apiRef.current = api;
            }}
            /*
              A "FONTOS" jeloles a formazo sor jobb vegen.

              Nem az also gombsorban, mert nem beszurando tartalom (mint az
              emoji vagy a fajl), hanem az egesz uzenetrol szolo dontes - azt
              pedig iras ELOTT hozza meg az ember. Korabban kulon sorban allt
              a mezo tetejen; a formazo gombok mellett ugyanott latszik, es a
              szerkeszto egy sorral alacsonyabb.
            */
            toolbarEnd={
              <span className="flex items-center gap-1">
                <label
                  // Telefonon csak a jelolo es az ikon fer ki a formazo gombok mellett.
                  title="Fontos üzenet"
                  className={[
                    'flex cursor-pointer items-center gap-1.5 rounded-pill border px-2 py-0.5',
                    'text-2xs font-medium transition-colors duration-(--duration-quick)',
                    important
                      ? 'border-accent bg-accent text-accent-ink'
                      : 'border-border-soft text-ink-faint hover:border-border-strong hover:text-ink-muted',
                  ].join(' ')}
                >
                  <input
                    type="checkbox"
                    checked={important}
                    onChange={(event) => setImportant(event.target.checked)}
                    className="size-3 accent-[var(--accent)]"
                  />
                  <span aria-hidden="true">
                    <Icon name="figyelmeztetes" size="sm" />
                  </span>
                  <span className="sr-only sm:not-sr-only">Fontos</span>
                </label>
                <label
                  title="Feladat - a csatorna fejlécében kipipálható"
                  className={[
                    'flex cursor-pointer items-center gap-1.5 rounded-pill border px-2 py-0.5',
                    'text-2xs font-medium transition-colors duration-(--duration-quick)',
                    task
                      ? 'border-accent bg-accent text-accent-ink'
                      : 'border-border-soft text-ink-faint hover:border-border-strong hover:text-ink-muted',
                  ].join(' ')}
                >
                  <input
                    type="checkbox"
                    checked={task}
                    onChange={(event) => setTask(event.target.checked)}
                    className="size-3 accent-[var(--accent)]"
                  />
                  <span aria-hidden="true">
                    <Icon name="pipa" size="sm" />
                  </span>
                  <span className="sr-only sm:not-sr-only">Feladat</span>
                </label>
              </span>
            }
          />
        </div>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          hidden
          onChange={(e) => {
            pickFiles(e.target.files);
            e.target.value = '';
          }}
        />
        {/* Telefonon kozvetlenul a kamera nyilik - nem a galeria, majd onnan a kamera (K2). */}
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          hidden
          onChange={(e) => {
            pickFiles(e.target.files);
            e.target.value = '';
          }}
        />

        <div
          className={`flex items-center px-1 pt-1.5 text-2xs text-ink-faint ${compact ? 'gap-1.5' : 'gap-3'}`}
        >
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            title={`Fájl csatolása (max ${MAX_FILES_PER_MESSAGE} db)`}
            className="rounded px-1.5 py-0.5 font-medium text-ink-muted transition-colors duration-(--duration-quick) hover:bg-surface-hover hover:text-ink"
          >
            <Icon name="csatolmany" /> Fájl
          </button>

          <button
            type="button"
            onClick={() => cameraInputRef.current?.click()}
            title="Fénykép készítése"
            className="hidden rounded px-1.5 py-0.5 font-medium text-ink-muted transition-colors duration-(--duration-quick) pointer-coarse:inline-block hover:bg-surface-hover hover:text-ink"
          >
            <Icon name="fenykep" /> Fotó
          </button>

          <EmojiPicker
            onPick={(emoji) => apiRef.current?.insert(emoji)}
            focusOnClose={() => apiRef.current?.focus()}
          >
            <button
              type="button"
              title="Emoji beszúrása"
              className="rounded px-1.5 py-0.5 font-medium text-ink-muted transition-colors duration-(--duration-quick) hover:bg-surface-hover hover:text-ink"
            >
              <Icon name="emoji" /> Emoji
            </button>
          </EmojiPicker>

          {/* A GIF a kuldendo mezobe kerul (lasd `pendingGif`), nem megy el azonnal. */}
          <GifPicker onPick={setPendingGif} focusOnClose={() => apiRef.current?.focus()}>
            <button
              type="button"
              title="GIF keresése"
              className="rounded px-1.5 py-0.5 font-medium text-ink-muted transition-colors duration-(--duration-quick) hover:bg-surface-hover hover:text-ink"
            >
              GIF
            </button>
          </GifPicker>

          {onPoll && (
            <button
              type="button"
              onClick={onPoll}
              title="Szavazás indítása"
              className="rounded px-1.5 py-0.5 font-medium text-ink-muted transition-colors duration-(--duration-quick) hover:bg-surface-hover hover:text-ink"
            >
              📊 Szavazás
            </button>
          )}
          {/*
            A BILLENTYU-SUGO csak asztalon.

            Telefonon harom sorba tordelt es elvitte a kepernyo aljat - ott
            raadasul nincs is Shift+Enter. Akinek szol, annak van billentyuzete.
          */}
          <span className={compact ? 'hidden' : 'hidden sm:inline'}>
            <Key>Enter</Key> küldés · <Key>Shift</Key>+<Key>Enter</Key> új sor · <Key>@</Key>{' '}
            említés
          </span>

          {onSchedule && (
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <button
                  type="button"
                  className="ml-auto rounded px-1.5 py-0.5 font-medium text-ink-muted transition-colors duration-(--duration-quick) hover:bg-surface-hover hover:text-ink"
                >
                  <Icon name="ora" /> Küldés később
                </button>
              </DropdownMenu.Trigger>

              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  align="end"
                  sideOffset={6}
                  className="animate-fade-rise z-50 min-w-56 rounded-card border border-border-soft bg-surface-raised p-1 shadow-(--shadow-modal)"
                >
                  {SCHEDULE_PRESETS.map((preset) => {
                    const when = resolvePreset(preset);
                    return (
                      <DropdownMenu.Item
                        key={preset.label}
                        onSelect={() => void schedule(when)}
                        className="flex cursor-pointer items-center justify-between gap-3 rounded-control px-2.5 py-1.5 text-sm outline-none data-[highlighted]:bg-surface-hover"
                      >
                        <span>{preset.label}</span>
                        <span className="text-2xs text-ink-faint">
                          {when.toLocaleString('hu-HU', {
                            weekday: 'short',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                      </DropdownMenu.Item>
                    );
                  })}

                  {/*
                  EGYEDI IDOPONT.

                  Az ajanlatok a gyakori eseteket fedik; ami nem fer bele
                  (jovo heti szerda delelott), az ide tartozik. A parbeszedet
                  a menu bezarasa UTAN nyitjuk - ket egymasra nyilo reteg
                  fokusz-csapdaja egymassal versenyezne.
                */}
                  <DropdownMenu.Separator className="my-1 h-px bg-border-soft" />
                  <DropdownMenu.Item
                    onSelect={() => setCustomSchedule('once')}
                    className="flex cursor-pointer items-center gap-2 rounded-control px-2.5 py-1.5 text-sm outline-none data-[highlighted]:bg-surface-hover"
                  >
                    <Icon name="ora" size="sm" /> Egyedi időpont…
                  </DropdownMenu.Item>
                  {/* ISMETLODO: heti beosztas, havi zaras - versenyaudit U9. */}
                  <DropdownMenu.Item
                    onSelect={() => setCustomSchedule('repeat')}
                    className="flex cursor-pointer items-center gap-2 rounded-control px-2.5 py-1.5 text-sm outline-none data-[highlighted]:bg-surface-hover"
                  >
                    <Icon name="ora" size="sm" /> Ismétlődő üzenet…
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          )}

          {/*
        A parbeszed a menun KIVUL el.

        Radix menuben maradva a menu bezarasa vinne magaval a parbeszedet is -
        igy viszont a valasztas kozben nyugodtan eltunhet a menu.
      */}
          {customSchedule && (
            <ScheduleDialog
              repeating={customSchedule === 'repeat'}
              onCancel={() => setCustomSchedule(false)}
              onPick={(sendAt, repeat) => {
                setCustomSchedule(false);
                void schedule(sendAt, repeat);
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function Key({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="rounded border border-border-soft bg-surface-raised px-1 font-sans text-2xs">
      {children}
    </kbd>
  );
}

/**
 * A GIF hozzafuzese a szerkeszto tartalmahoz: a szoveg utan, egy uzenetben.
 * Ures szerkesztonel csak a GIF megy (ures bekezdes nelkul).
 */
function withGif(value: ComposerSubmit, gif: GifResult): ComposerSubmit {
  const node = {
    type: 'gif' as const,
    attrs: {
      url: gif.url,
      previewUrl: gif.previewUrl,
      stillUrl: gif.stillUrl,
      title: gif.title,
      width: gif.width,
      height: gif.height,
    },
  };
  const label = `[GIF${gif.title ? `: ${gif.title}` : ''}]`;
  const hasText = value.text.trim().length > 0;
  return {
    text: hasText ? `${value.text}\n${label}` : label,
    body: {
      type: 'doc',
      content: hasText ? [...(value.body.content ?? []), node] : [node],
    },
  };
}
